import { getToken, deleteToken, onMessage } from "firebase/messaging";
import { detect } from "detect-browser";
import { getMessagingIfSupported, messagingConfig, FCM_VAPID_KEY } from "../config/messagingConfig";
import SettingHandler from "../Apihander/setting/settingHandler";

// The worker cannot read .env, so the same config is handed to it on the registration URL.
const SW_PATH = `/firebase-messaging-sw.js?${new URLSearchParams(messagingConfig).toString()}`;
const DEVICE_ID_KEY = "pushDeviceId";
const FCM_TOKEN_KEY = "fcmToken";

const skip = (reason) => {
    console.log(`Push not enabled: ${reason}`);
    return null;
};

// A stored id keeps one row per browser instead of a new one on every registration.
const getDeviceId = () => {
    const stored = localStorage.getItem(DEVICE_ID_KEY);

    if (stored) {
        return stored;
    }

    const deviceId = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);

    return deviceId;
};

// Waits on this exact registration, since a replaced worker sits in "waiting" and
// navigator.serviceWorker.ready would never resolve for it.
const waitForActiveWorker = (registration) => new Promise((resolve) => {
    const worker = registration.installing || registration.waiting;

    if (registration.active || !worker) {
        resolve();
        return;
    }

    const onStateChange = () => {
        if (worker.state === "activated") {
            worker.removeEventListener("statechange", onStateChange);
            resolve();
        }
    };

    worker.addEventListener("statechange", onStateChange);
    // Called once by hand, in case the worker activated before the listener was attached.
    onStateChange();
});

// FCM rotates the token on its own, so the backend is only told when it actually changed.
const syncDeviceToken = async (fcmToken) => {
    if (localStorage.getItem(FCM_TOKEN_KEY) === fcmToken) {
        return;
    }

    const browser = detect();
    // The backend groups tokens by deviceType, and detect() reports "browser" for every desktop browser.
    const response = await SettingHandler.doRegisterDeviceToken({
        deviceType: browser?.type || "browser",
        deviceName: browser?.name || "unknown",
        deviceId: getDeviceId(),
        fcmToken,
    });

    // Only a stored token is cached, so a failed call is retried instead of going quiet.
    if (response?.statusCode === 200) {
        localStorage.setItem(FCM_TOKEN_KEY, fcmToken);
        return;
    }

    console.log(response, "auth/device rejected the token");
};

// Call from a click: browsers reject a permission prompt that no user gesture asked for.
export const enablePushNotifications = async () => {
    if (!FCM_VAPID_KEY) {
        return skip("REACT_APP_FCM_VAPID_KEY is not set");
    }

    // isSupported() already covers serviceWorker, PushManager and Notification.
    const messaging = await getMessagingIfSupported();

    if (!messaging) {
        return skip("this browser does not support web push");
    }

    // Chrome remembers a block for the origin, so requestPermission never prompts again.
    if (Notification.permission === "denied") {
        return skip("notifications are blocked for this site");
    }

    if (Notification.permission !== "granted" && (await Notification.requestPermission()) !== "granted") {
        return skip("permission was not granted");
    }

    try {
        const registration = await navigator.serviceWorker.register(SW_PATH);
        // register() resolves while the worker is still installing, but getToken() subscribes
        // through PushManager, which fails outright unless a worker is already activated.
        await waitForActiveWorker(registration);

        const fcmToken = await getToken(messaging, {
            vapidKey: FCM_VAPID_KEY,
            serviceWorkerRegistration: registration,
        });

        if (!fcmToken) {
            return skip("FCM returned no registration token");
        }

        await syncDeviceToken(fcmToken);

        return fcmToken;
    } catch (error) {
        console.log(error, "push registration");
        return null;
    }
};

// Boot path: picks up a rotated token without ever showing a prompt.
export const syncExistingPushToken = () =>
    ("Notification" in window && Notification.permission === "granted") ? enablePushNotifications() : null;

export const onForegroundMessage = async (handler) => {
    const messaging = await getMessagingIfSupported();

    return messaging ? onMessage(messaging, handler) : null;
};

// FCM draws nothing while any tab of the app is visible, so a tab the user is not actually
// looking at raises its own banner. An unfocused window still counts as visible, which is why
// hasFocus() is checked too: the app is on screen but the user is in another application.
export const showBackgroundNotification = async (payload) => {
    if ((!document.hidden && document.hasFocus()) || Notification.permission !== "granted") {
        return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    const { title, body } = payload?.notification || {};

    // Tagged with the recording id, so several hidden tabs collapse into one banner.
    registration?.showNotification(title || "Voice Recorder", {
        body,
        icon: "/logo192.png",
        tag: payload?.data?.audioId,
        data: payload?.data || {},
    });
};

// The token is dropped on logout so the next account does not inherit this browser's pushes.
export const disablePushNotifications = async () => {
    try {
        const messaging = await getMessagingIfSupported();

        if (messaging) {
            await deleteToken(messaging);
        }
    } catch (error) {
        console.log(error, "fcm token delete");
    }

    localStorage.removeItem(FCM_TOKEN_KEY);
};
